import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  Person,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { getOrganizationMenu, hasPermission } from '../../data/menuData';
import { useOrganization } from '../../context/OrganizationContext';
import { useProfile } from '../../context/userContext';
import OrganizationSelector from '../organization/OrganizationSelector';
import styles from '../../styles/OrganizationSidebar.module.css';

const OrganizationSidebar = ({ collapsed = false, onCollapsedChange, onNavigate, isInDrawer = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, getAccessTokenSilently } = useAuth0();
  const { profile } = useProfile();
  const { currentOrganization, userRole, subscriptionLimits, isOrganizationUser } = useOrganization();

  const [expandedSections, setExpandedSections] = useState({});
  const [fleetUsage, setFleetUsage] = useState({ droneCount: 0, batteryCount: 0, payloadCount: 0 });

  // Fetch fleet usage counts
  useEffect(() => {
    const fetchFleetUsage = async () => {
      if (!currentOrganization?.organizationId) return;

      try {
        const token = await getAccessTokenSilently();

        // Fetch drones count
        const dronesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/drones`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Fetch batteries count
        const batteriesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/batteries`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Fetch payloads count
        const payloadsResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/payloads`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const dronesData = dronesResponse.ok ? await dronesResponse.json() : { drones: [] };
        const batteriesData = batteriesResponse.ok ? await batteriesResponse.json() : { batteries: [] };
        const payloadsData = payloadsResponse.ok ? await payloadsResponse.json() : { payloads: [] };

        setFleetUsage({
          droneCount: dronesData.total ?? dronesData.drones?.length ?? 0,
          batteryCount: batteriesData.total ?? batteriesData.batteries?.length ?? 0,
          payloadCount: payloadsData.total ?? payloadsData.payloads?.length ?? 0
        });
      } catch (error) {
      }
    };

    fetchFleetUsage();
  }, [currentOrganization, getAccessTokenSilently, location.pathname]);

  // Get organization menu from menuData
  const navigationItems = getOrganizationMenu(userRole, currentOrganization, subscriptionLimits, fleetUsage);

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleSectionToggle = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleKeyDown = (event, item) => {
    // Keyboard navigation support (WCAG 2.1)
    // Use Tab/Shift+Tab to navigate between menu items
    // Use Enter/Space to activate items or toggle submenus
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (item.hasSubmenu && item.submenu && item.submenu.length > 0) {
        handleSectionToggle(item.id);
      } else if (item.path) {
        handleNavigation(item.path);
      }
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isParentActive = (item) => {
    if (!item.submenu) return false;
    return item.submenu.some(sub => location.pathname === sub.path);
  };

  const renderDivider = (index) => (
    <div key={`divider-${index}`} className={styles['nav-divider']} role="separator" aria-hidden="true" />
  );

  const renderNavItem = (item, index, level = 0) => {
    // Skip dividers
    if (item.type === 'divider') {
      return renderDivider(index);
    }

    // Check permission using hasPermission helper
    if (item.permission && !hasPermission(userRole, item.permission)) {
      return null;
    }

    const active = item.path && isActive(item.path);
    const expanded = expandedSections[item.id];
    const hasChildren = item.hasSubmenu && item.submenu && item.submenu.length > 0;
    const hasActiveChild = isParentActive(item);

    // Handle submenu items
    if (hasChildren) {
      // When collapsed, show as regular link with tooltip
      if (collapsed) {
        return (
          <li key={item.id} role="none">
            <Tooltip title={item.title} placement="right">
              <NavLink
                to={item.path || '#'}
                onClick={() => onNavigate?.()}
                className={`${styles['nav-button']} ${hasActiveChild || active ? styles['nav-button-active'] : ''}`}
                aria-label={item.ariaLabel || item.title}
                aria-current={active ? 'page' : undefined}
                role="menuitem"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '48px' }}
              >
                <span className={styles['nav-icon']}>
                  {hasActiveChild || active ? (item.iconFill || item.icon) : item.icon}
                </span>
              </NavLink>
            </Tooltip>
          </li>
        );
      }

      return (
        <li key={item.id} className={styles['nav-item']}>
          <div
            className={`${styles['nav-button']} ${hasActiveChild ? styles['nav-button-active'] : ''}`}
            onClick={() => handleSectionToggle(item.id)}
            onKeyDown={(e) => handleKeyDown(e, item)}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={item.ariaLabel || item.title}
            aria-controls={`submenu-${item.id}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span className={styles['nav-icon']}>
                {hasActiveChild ? (item.iconFill || item.icon) : item.icon}
              </span>
              <span className={styles['nav-text']}>{item.title}</span>
            </div>
            <span className={styles['nav-icon']}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </span>
          </div>

          {/* Submenu */}
          <ul
            id={`submenu-${item.id}`}
            className={`${styles['submenu']} ${expanded ? styles['submenu-expanded'] : styles['submenu-collapsed']}`}
            role="menu"
            aria-label={`${item.title} submenu`}
          >
            {item.submenu.map((subItem, subIndex) => {
              // Check permission for submenu items
              if (subItem.permission && !hasPermission(userRole, subItem.permission)) {
                return null;
              }

              const subActive = isActive(subItem.path);
              return (
                <li key={subItem.id} role="none">
                  <NavLink
                    to={subItem.path}
                    onClick={() => onNavigate?.()}
                    className={`${styles['nav-button']} ${styles['nav-button-child']} ${subActive ? styles['nav-button-active'] : ''}`}
                    aria-label={subItem.ariaLabel || subItem.title}
                    aria-current={subActive ? 'page' : undefined}
                    role="menuitem"
                  >
                    <span className={styles['nav-text-child']}>{subItem.title}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </li>
      );
    }

    // Regular menu item
    return (
      <li key={item.id} role="none" className={styles['nav-item']}>
        <Tooltip title={collapsed ? item.title : ''} placement="right">
          <NavLink
            to={item.path}
            onClick={() => onNavigate?.()}
            className={`${styles['nav-button']} ${active ? styles['nav-button-active'] : ''} ${level > 0 ? styles['nav-button-child'] : ''}`}
            aria-label={item.ariaLabel || item.title}
            aria-current={active ? 'page' : undefined}
            role="menuitem"
            style={collapsed ? { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '48px' } : { display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <span className={styles['nav-icon']}>
              {active ? (item.iconFill || item.icon) : item.icon}
            </span>
            {!collapsed && (
              <span className={level > 0 ? styles['nav-text-child'] : styles['nav-text']}>
                {item.title}
              </span>
            )}
          </NavLink>
        </Tooltip>
      </li>
    );
  };

  if (!isOrganizationUser) {
    return null; // Don't show org sidebar if not an organization user
  }

  return (
    <nav
      className={`${styles['sidebar-container']} ${collapsed ? styles['sidebar-collapsed'] : ''} ${isInDrawer ? styles['sidebar-in-drawer'] : ''}`}
      aria-label="Organization navigation"
    >
      {/* Collapse/Expand Button - Very Top */}
      <div className={styles['collapse-button-container']}>
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={() => onCollapsedChange?.(!collapsed)}
            className={styles['collapse-button']}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            size="small"
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Organization Header Content */}
      <div className={styles['sidebar-header']}>
        {!collapsed && (
          <div className={styles['header-content']}>
            {/* Row 1: Organization Selector */}
            <div className={styles['selector-wrapper']}>
              <OrganizationSelector collapsed={false} />
            </div>

            {/* Row 2: Organization Details */}
            <div className={styles['org-details']}>
              <p className={styles['org-metadata']}>
                {userRole === 'owner'
                  ? `${currentOrganization?.subscription?.planName || 'No Plan'} · ${userRole.charAt(0).toUpperCase() + userRole.slice(1)}`
                  : (userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'Member')
                }
              </p>
              <p className={styles['user-email']}>
                {profile?.email}
              </p>
            </div>
          </div>
        )}

        {/* Collapsed state - show just the selector button */}
        {collapsed && (
          <div className={styles['selector-wrapper-collapsed']}>
            <OrganizationSelector collapsed={true} />
          </div>
        )}
      </div>

      {/* Menu items */}
      <ul className={styles['nav-section']} role="menu">
        {navigationItems.map((item, index) => renderNavItem(item, index))}
      </ul>

      {/* Account footer */}
      <div className={styles['sidebar-footer']}>
        <Divider className={styles['footer-divider']} />
        <div className={styles['account-actions']}>
          <Tooltip title="View Profile" placement={collapsed ? "right" : "top"}>
            <NavLink
              to="/profile"
              onClick={() => onNavigate?.()}
              className={styles['footer-action']}
              aria-label="View Profile"
            >
              <Person fontSize="small" />
              {!collapsed && <span className={styles['footer-action-text']}>Profile</span>}
            </NavLink>
          </Tooltip>
          <Tooltip title="Logout" placement={collapsed ? "right" : "top"}>
            <button
              onClick={handleLogout}
              className={styles['footer-action']}
              aria-label="Logout"
            >
              <LogoutIcon fontSize="small" />
              {!collapsed && <span className={styles['footer-action-text']}>Logout</span>}
            </button>
          </Tooltip>
        </div>
      </div>
    </nav>
  );
};

export default OrganizationSidebar;
